import { Box } from '@customink/pigment-react';
import { useConfigContext } from '@customink/project-service-react-components';
import type {
  Design,
  ProductColorId,
} from '@customink/project-service-react-components';
import Skeleton from '@mui/material/Skeleton';
import type { SxProps } from '@mui/system/styleFunctionSx';
import { useCallback, useState } from 'react';

import ErrorBoundary from '@/components/ErrorBoundary';
import Image from '@/components/Image';
import { ProductImageModal } from '@/components/ProductImageModal';
import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';
import { proofBackground } from '@/utils/designTokens';

export default function ProofImage({
  cid,
  colorId,
  thumbnailView,
  view = 'front_medium_extended.png',
  withLegacyOptions,
  draft = false,
  sx,
  isProjectItemProof = false,
}: {
  cid?: Design['cid'] | null;
  colorId?: ProductColorId;
  thumbnailView?: string;
  view: string;
  withLegacyOptions?: boolean;
  draft?: boolean;
  sx?: SxProps;
  isProjectItemProof?: boolean;
}) {
  const url = useConfigContext().imagesUrl;
  const [swapped, setSwapped] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const legacyOptions = withLegacyOptions
    ? '&pblegacy=1&pblegacysize=small&pblegacywm=1'
    : '';
  const cidParams = cid ? `?design=${cid}${legacyOptions}` : '';

  const imageSrc = `${url}/colors/${colorId}/views/alt/${view}${cidParams}`;
  const thumbnailSrc = thumbnailView
    ? `${url}/colors/${colorId}/views/alt/${thumbnailView}${cidParams}`
    : undefined;

  // NTH: previous color proof shows, show skeleton until new color proof is retrieved
  const showSkeleton = !url || !colorId;

  const onMainImageClick = useCallback(() => {
    if (isProjectItemProof) {
      const currentView = swapped && thumbnailView ? thumbnailView : view;
      const viewType = currentView.toLowerCase().includes('front')
        ? 'front'
        : 'back';

      googleAnalyticsTag({
        event: 'cart_design_proof',
        interaction_type: 'click thumbnail',
        design_proof_view: `expand ${viewType}`,
      });

      setIsModalOpen(true);
    }
  }, [isProjectItemProof, swapped, thumbnailView, view]);

  const onClick = useCallback(() => {
    setSwapped((current) => !current);
    if (isProjectItemProof) {
      googleAnalyticsTag({
        event: 'cart_design_proof',
        interaction_type: 'click rotate',
      });
    }
  }, [isProjectItemProof]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Determine which view to show in the modal based on what's currently shown in the main image
  const modalView =
    swapped && thumbnailView
      ? thumbnailView.replace('medium', 'large')
      : view.replace('medium', 'large');

  return (
    <ErrorBoundary>
      <Box
        alignSelf={showSkeleton ? 'stretch' : undefined}
        data-testid="proof-image"
        display="flex"
        position="relative"
        sx={{
          aspectRatio: '1 / 1',
          opacity: draft ? 0.3 : 1,
          ...sx,
        }}
      >
        {showSkeleton ? (
          <Skeleton
            data-testid="proof-image-skeleton"
            height="100%"
            variant="rectangular"
            width="100%"
          />
        ) : (
          <>
            <Image
              onClick={onMainImageClick}
              src={swapped ? thumbnailSrc : imageSrc}
              style={{
                width: '100%',
                backgroundColor: proofBackground,
                objectFit: 'cover',
                cursor: 'zoom-in',
              }}
            />
            {thumbnailView ? (
              <Image
                onClick={onClick}
                src={swapped ? imageSrc : thumbnailSrc}
                style={{
                  position: 'absolute',
                  bottom: '4px',
                  right: '4px',
                  objectFit: 'cover',
                  height: '36px',
                  aspectRatio: '1 / 1',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgb(0 0 0 / 40%)',
                  backgroundColor: proofBackground,
                  cursor: 'pointer',
                }}
              />
            ) : null}
          </>
        )}
        <ProductImageModal
          cid={cid}
          colorId={colorId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          view={modalView}
        />
      </Box>
    </ErrorBoundary>
  );
}
